"use client"

// Import React hooks for side effects and state management
import { useEffect, useState } from "react"

// Global flags to track if the Navattic script is loaded or loading
// These ensure the script is only loaded once across the app lifecycle
let navatticScriptLoaded = false
let navatticScriptLoading = false
let loadingPromise: Promise<void> | null = null

/**
 * Custom React hook to dynamically load the Navattic embed script.
 * Ensures the script is loaded only once, even if multiple components use this hook.
 * Returns a boolean indicating whether the script is loaded and ready.
 */
function useNavatticEmbedScript() {
  // Local state to track if the script is loaded for this component
  const [loaded, setLoaded] = useState(navatticScriptLoaded)

  useEffect(() => {
    // If script is already loaded, update state and exit
    if (navatticScriptLoaded) {
      setLoaded(true)
      return
    }

    // If script is currently loading, attach to the existing promise
    if (navatticScriptLoading && loadingPromise) {
      loadingPromise.then(() => setLoaded(true))
      return
    }

    // Mark script as loading
    navatticScriptLoading = true

    const embedSrc = "https://js.navattic.com/embeds.js"

    // Function to load the Navattic script asynchronously
    const loadNavatticScript = async () => {
      try {
        // If NavatticEmbed is already available on window, mark as loaded
        if (typeof window !== "undefined" && (window as any).NavatticEmbed) {
          navatticScriptLoaded = true
          navatticScriptLoading = false
          setLoaded(true)
          return
        }

        // Check if the script tag is already present in the DOM
        const existingScript = document.querySelector(
          `script[src="${embedSrc}"]`,
        )
        if (!existingScript) {
          // If not, create and append the script tag
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement("script")
            script.src = embedSrc
            script.async = true
            script.onload = () => {
              // Once loaded, initialize Navattic embeds if available
              if ((window as any).NavatticEmbed) {
                ;(window as any).NavatticEmbed.loadEmbeds()
              }
              resolve()
            }
            script.onerror = () => {
              // Handle script load failure
              console.warn("Failed to load Navattic embed script")
              reject(new Error("Failed to load Navattic embed script"))
            }
            document.body.appendChild(script)
          })
        }
        // If script already exists, attempt to initialize embeds
        if ((window as any).NavatticEmbed) {
          ;(window as any).NavatticEmbed.loadEmbeds()
        }
        // Mark as loaded and update state
        navatticScriptLoaded = true
        navatticScriptLoading = false
        setLoaded(true)
      } catch (error) {
        // Log and handle any errors during script loading
        console.warn("Failed to load Navattic embed script", error)
        navatticScriptLoading = false
      }
    }

    // Store the loading promise so other hook instances can await it
    loadingPromise = loadNavatticScript()
  }, [])

  // Return whether the script is loaded
  return loaded
}

export default useNavatticEmbedScript
